import { assertInInjectionContext, inject, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';

import { readClarityWorkspaceRoute } from './clarity-workspace-route.util';

/**
 * Whether a workspace tab is on screen, read off the router's URL after every completed navigation.
 *
 * **What a tab's reads are gated on (EFF-26616 — N3, N5).** A tab's services are provided by its route, and
 * Angular keeps a route's environment injector for as long as the application runs: leaving the tab destroys
 * its component, never its services. A read keyed on the selected domain alone therefore kept polling, and
 * re-read on every domain switch, while the reader was on another tab. The tab being on screen is the
 * condition brain gets for free by unmounting it.
 *
 * When a tab's services are first built decides what the first value says. Built by the tab's component, or by
 * anything it injects, they read the new URL — the router moves it before it activates the route — so a first
 * visit and a deep link both start `true`. Built with the route's injector itself, by an environment
 * initializer, they read the URL the reader came from, because that injector is created when the route is
 * matched; such a caller must not read its first value as a departure.
 *
 * Call it in an injection context, like `inject()`.
 *
 * @param sectionPath The Clarity section's own path segment, as `readClarityWorkspaceRoute` takes it.
 * @param tab The tab's route segment.
 * @returns `true` while the router's URL is that tab of a workspace — `/<section>/d/<domainId>/<tab>[/…]`.
 */
export function injectClarityWorkspaceTabActive(sectionPath: string, tab: string): Signal<boolean> {
	assertInInjectionContext(injectClarityWorkspaceTabActive);

	const router = inject(Router);
	const isOnTab = (url: string): boolean => readClarityWorkspaceRoute(url, sectionPath).params.tab === tab;

	return toSignal(
		router.events.pipe(
			filter((event): event is NavigationEnd => event instanceof NavigationEnd),
			map((): boolean => isOnTab(router.url)),
		),
		{ initialValue: isOnTab(router.url) },
	);
}
